'use client'

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Formik, Form } from 'formik'
import { useTranslations } from 'next-intl'

import { Heading, Paragraph } from '../../atoms/Typography/Typography.component'
import { Container, SpacerBase } from '../../atoms/GridSystem'
import { FadeSlideUp } from '../../../theme/animations'

import { Stepper, StepperList, StepperStep, StepperContent, StepperControls } from '../Stepper/Stepper.component'
import useStepper from '../Stepper/useStepper'

import LeadFormStep1 from './LeadFormStep1.component'
import LeadFormStep2 from './LeadFormStep2.component'
import LeadFormStep3 from './LeadFormStep3.component'

import { SectionThemeContext } from '../../../contexts/SectionTheme.context'
import { useModal } from '../../../contexts/Modal.context'
import ModalAuth from '../../molecules/Modal/ModalAuth/ModalAuth.component'
import * as S from './LeadForm.styles'
import {
  D365_BUNDLE_URL,
  D365_FORM_API_URL,
  LEAD_FORM_CRM_ID,
  buildLeadFormCrmMappings,
  type CrmFieldMapping,
} from './LeadForm.constants'
import { INITIAL_VALUES, buildValidationSchema, STEP_FIELDS } from './LeadForm.utils'

type D365FormCapture = {
  serializeForm: (
    form: HTMLFormElement,
    mappings: CrmFieldMapping[]
  ) => { SerializedForm: { build: () => unknown } }
  submitForm: (
    config: { FormId: string; FormApiUrl: string },
    payload: unknown
  ) => Promise<void>
}

type LeadFormProps = {
  sectionId?: string | null
  withBackground?: boolean
  colorScheme?: string
}

function LeadForm({ sectionId, withBackground, colorScheme }: LeadFormProps) {
  const tRoot = useTranslations()
  const t = useTranslations('leadForm')
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const { showModal } = useModal()
  const activeColorScheme = colorScheme || selectedColorScheme
  const d365Ref = useRef<D365FormCapture | null>(null)

  useEffect(() => {
    const existing = document.querySelector(
      `script[src="${D365_BUNDLE_URL}"]`
    ) as HTMLScriptElement | null

    if (existing) {
      d365Ref.current = (window as any).d365mktformcapture ?? null
      return
    }

    const script = document.createElement('script')
    script.src = D365_BUNDLE_URL
    script.async = true
    script.onload = () => {
      d365Ref.current = (window as any).d365mktformcapture ?? null
    }
    document.head.appendChild(script)
  }, [])

  const steps = useMemo(() => [
    t('steps.aboutYou'),
    t('steps.companyInfo'),
    t('steps.additionalInfo'),
  ], [t])

  const stepper = useStepper(steps)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [submittedFirstName, setSubmittedFirstName] = useState('')

  const validationSchema = useMemo(() => buildValidationSchema(tRoot), [tRoot])

  const handleSubmit = async (values: typeof INITIAL_VALUES) => {
    setIsSubmitting(true)
    setSubmitError(false)

    try {
      const d365 = d365Ref.current
      if (!d365) throw new Error('D365 Form Capture script not loaded')

      const formElement = document.querySelector(
        '[data-crm-id="lead-form"]'
      ) as HTMLFormElement | null
      if (!formElement) throw new Error('Form element not found')

      const mappings = buildLeadFormCrmMappings(values)
      const serialized = d365.serializeForm(formElement, mappings)
      const payload = serialized.SerializedForm.build()

      await d365.submitForm(
        { FormId: LEAD_FORM_CRM_ID, FormApiUrl: D365_FORM_API_URL },
        payload
      )

      setSubmittedFirstName(values.firstName)
      setIsSubmitted(true)
    } catch {
      setSubmitError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSubmitted) {
    return (
      <S.LeadFormWrapper colorScheme={activeColorScheme} withBackground={withBackground}>
        <Container>
          <S.SuccessMessage aria-live="polite" role="status">
            <FadeSlideUp>
              <Heading typo="headingXXL" as="h2" content={t('success.thanks', { name: submittedFirstName })} />
            </FadeSlideUp>
            <FadeSlideUp $delay={0.15}>
              <Heading typo="headingXXL" as="h2" content={t('success.requestSent')} />
            </FadeSlideUp>
            <FadeSlideUp $delay={0.3}>
              <Heading typo="headingXXL" as="h2" content={t.raw('success.sentCorrectly')} />
            </FadeSlideUp>
            <SpacerBase xs={3} />
            <FadeSlideUp $delay={0.45}>
              <Paragraph typo="paragraphMD" content={t.raw('success.contactInfo')} />
            </FadeSlideUp>
          </S.SuccessMessage>
        </Container>
      </S.LeadFormWrapper>
    )
  }

  return (
    <S.LeadFormWrapper
      as="section"
      id={sectionId || undefined}
      colorScheme={activeColorScheme}
      withBackground={withBackground}
    >
      <Container>
        <Formik
          initialValues={INITIAL_VALUES}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ validateForm, setFieldTouched, values }) => {
            const handleStepNext = async () => {
              const errors = await validateForm()
              const currentFields = STEP_FIELDS[stepper.currentStep] || []
              const hasStepErrors = currentFields.some((field) => errors[field as keyof typeof errors])

              currentFields.forEach((field) => setFieldTouched(field, true))

              if (hasStepErrors) {
                const firstErrorField = currentFields.find(
                  (field) => errors[field as keyof typeof errors]
                )
                if (firstErrorField) {
                  const el = document.getElementById(firstErrorField)
                  el?.focus()
                }
                return
              }

              if (stepper.currentStep === 0 && values.subject === 'other') {
                showModal(ModalAuth as React.ComponentType, {
                  title: t('modal.title'),
                  description: t.raw('modal.description'),
                  cta: { label: t('modal.cta'), handleClick: () => {} },
                })

                return
              }

              stepper.next()
            }

            return (
              <Form noValidate data-crm-id="lead-form">
                <Stepper value={stepper.currentStep} onValueChange={stepper.goToStep} animated>
                  <StepperList aria-label={t('controls.formProgress')}>
                    {stepper.steps.map((title, index) => (
                      <StepperStep key={index} value={index}>
                        {title}
                      </StepperStep>
                    ))}
                  </StepperList>

                  <SpacerBase xs={4} />

                  <StepperContent value={0}>
                    <LeadFormStep1 withBackground={withBackground} />
                  </StepperContent>

                  <StepperContent value={1}>
                    <LeadFormStep2 withBackground={withBackground} />
                  </StepperContent>

                  <StepperContent value={2}>
                    <LeadFormStep3 withBackground={withBackground} />
                  </StepperContent>

                  {submitError ? (
                    <S.ErrorMessage role="alert">
                      <Paragraph typo="paragraphSM" content={tRoot('composableForm.errorSubmit')} />
                    </S.ErrorMessage>
                  ) : null}

                  <StepperControls
                    prevLabel={t('controls.prev')}
                    nextLabel={t('controls.next')}
                    submitLabel={t('controls.submit')}
                    isSubmitting={isSubmitting}
                    onNext={handleStepNext}
                  />
                </Stepper>
              </Form>
            )
          }}
        </Formik>
      </Container>
    </S.LeadFormWrapper>
  )
}

export default LeadForm
